import { useMemo } from "react"
import { toast } from "react-toastify"
import classNames from "classnames"

import { useLazyUpdateThemeQuery } from "store/api/dashboardApi"
import { useAppDispatch, useAppSelector } from "store/hooks"
import { setUserActiveTheme } from "store/tempDataSlise"
import { ContractorThemeKey, ContractorThemes } from "constants/ContractorThemes"

import { AccountPageShell } from "./shared"

import styles from "./index.module.scss"

export const ThemePage = () => {
  const dispatch = useAppDispatch()
  const userActiveTheme = useAppSelector(state => state.tempData.userActiveTheme)
  const [triggerUpdateTheme, { isFetching: isThemeUpdating }] = useLazyUpdateThemeQuery()

  const themeItems = useMemo(
    () =>
      Object.entries(ContractorThemes).map(([key, theme], index) => ({
        key: key as ContractorThemeKey,
        label: index === 0 ? "Default" : `Theme ${index + 1}`,
        gradient: theme.backgroundGradient,
      })),
    [],
  )

  const handleUpdateThemeError = (previousTheme: ContractorThemeKey) => {
    toast.error("Failed to update theme, please try again later")
    dispatch(setUserActiveTheme(previousTheme))
  }

  const handleThemeChange = async (themeId: ContractorThemeKey) => {
    const previousTheme = userActiveTheme
    dispatch(setUserActiveTheme(themeId))

    try {
      const result = await triggerUpdateTheme(themeId)

      if (result.error) {
        handleUpdateThemeError(previousTheme)
      }
    } catch {
      handleUpdateThemeError(previousTheme)
    }
  }

  return (
    <AccountPageShell>
      <h1 className={styles.title}>Choose a Theme</h1>
      <div className={styles.themeGrid}>
        {themeItems.map(theme => (
          <button
            key={theme.key}
            className={classNames(styles.themeButton, {
              [styles.themeButtonActive]: userActiveTheme === theme.key,
            })}
            type="button"
            onClick={() => handleThemeChange(theme.key)}
            disabled={isThemeUpdating}
          >
            <span className={styles.themeSwatch} style={{ background: theme.gradient }} />
            <span className={styles.themeName}>{theme.label}</span>
          </button>
        ))}
      </div>
    </AccountPageShell>
  )
}
